import { useState } from "react";
import { useCustom, useTranslate } from "@refinedev/core";
import { AmbulanceIcon, HomeIcon } from "@fawkes/ui";

import { EmptyState, LoadingState } from "../../components/states";
import { FosterError } from "./FosterError";
import type { Page } from "../../shell/nav";
import { AuthImage } from "./AuthImage";
import { ClockIcon, MailIcon, PhoneIcon } from "./icons";
import type { PortalAddress, PortalAnimals, PortalContact, PortalOrganizationContact } from "./types";

// jmix-8.4: Contact — one card per linked organization (legacy ContactView), federated OrganizationInfo +
// the authenticated logo stream. Design: per organization the name + "pleeggezin actief"-badge, address,
// phone and e-mail on the left, the 24/7 emergency number as a red block on the right, and quick actions
// (Bel / E-mail / Meer details). Per-organization graceful degradation: an unreachable organization still
// renders a card so one dead backoffice never blanks the others.

function formatAddress(address: PortalAddress | null): string | null {
  if (!address) {
    return null;
  }
  const line = [address.street, [address.postalCode, address.city].filter(Boolean).join(" ")]
    .filter((p) => p && p.trim().length > 0)
    .join(", ");
  return line.length > 0 ? line : null;
}

function ChevronIcon() {
  return (
    <svg viewBox="0 0 24 24" width={16} height={16} fill="none" stroke="currentColor" strokeWidth={2.2}
      strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polyline points="9 6 15 12 9 18" />
    </svg>
  );
}

function OrganizationCard({
  organization,
  heeftDieren,
}: {
  organization: PortalOrganizationContact;
  heeftDieren: boolean;
}) {
  const t = useTranslate();
  const [detailsOpen, setDetailsOpen] = useState(false);
  const address = formatAddress(organization.address);

  return (
    <section className="contact-card">
      <div className="contact-card__main">
        <div className="contact-card__logo">
          <AuthImage
            path={organization.hasLogo ? `/portal/organizations/${organization.organizationUuid}/logo` : null}
            alt={t("portal.contact.logoAlt")}
            size="56px"
            className="fawkes-portal__logo"
          />
        </div>
        <div className="contact-card__info">
          <h2 className="contact-card__name">
            {organization.name || t("portal.contact.fallbackName")}
          </h2>
          <span className={"contact-card__badge" + (heeftDieren ? " is-actief" : "")}>
            {heeftDieren ? "✓ " : "● "}
            {heeftDieren ? t("portal.contact.badge.active") : t("portal.contact.badge.noAnimals")}
          </span>

          {!organization.reachable ? (
            <p className="fawkes-portal__muted" role="status">
              {t("portal.contact.unreachable")}
            </p>
          ) : (
            <ul className="fawkes-portal__list contact-card__rows">
              {address ? (
                <li className="fawkes-portal__row">
                  <HomeIcon width={16} height={16} />
                  <span>{address}</span>
                </li>
              ) : null}
              {organization.phone ? (
                <li className="fawkes-portal__row">
                  <PhoneIcon width={16} height={16} />
                  <a href={`tel:${organization.phone}`}>{organization.phone}</a>
                </li>
              ) : null}
              {organization.email ? (
                <li className="fawkes-portal__row">
                  <MailIcon width={16} height={16} />
                  <a href={`mailto:${organization.email}`}>{organization.email}</a>
                </li>
              ) : null}
            </ul>
          )}
        </div>

        <div className="contact-card__zij">
          {organization.emergencyPhone ? (
            <div className="contact-card__spoed" role="note">
              <div className="contact-card__spoed-kop">
                <AmbulanceIcon width={18} height={18} />
                {t("portal.contact.emergency.title")}
              </div>
              <a className="contact-card__spoed-nummer" href={`tel:${organization.emergencyPhone}`}>
                {organization.emergencyPhone}
              </a>
              <p className="contact-card__spoed-noot">{t("portal.contact.emergency.note")}</p>
            </div>
          ) : null}
          <div className="contact-card__acties">
            {organization.phone ? (
              <a className="contact-card__knop" href={`tel:${organization.phone}`}>
                <PhoneIcon width={15} height={15} /> {t("portal.contact.call")}
              </a>
            ) : null}
            {organization.email ? (
              <a className="contact-card__knop" href={`mailto:${organization.email}`}>
                <MailIcon width={15} height={15} /> {t("portal.contact.mail")}
              </a>
            ) : null}
            <button
              type="button"
              className="contact-card__knop contact-card__knop--details"
              onClick={() => setDetailsOpen((v) => !v)}
              aria-expanded={detailsOpen}
            >
              {t("portal.contact.details")} <ChevronIcon />
            </button>
          </div>
        </div>
      </div>

      {detailsOpen ? (
        <div className="contact-card__details">
          {organization.openingHours ? (
            <div className="fawkes-portal__row">
              <ClockIcon width={16} height={16} />
              <span>
                <strong>{t("portal.contact.openingHours")}:</strong> {organization.openingHours}
              </span>
            </div>
          ) : (
            <p className="fawkes-portal__muted">{t("portal.contact.noDetails")}</p>
          )}
        </div>
      ) : null}
    </section>
  );
}

export function Contact({ onNavigate }: { onNavigate?: (page: Page) => void } = {}) {
  const t = useTranslate();
  const { data, isLoading, isError, refetch } = useCustom<PortalContact>({
    url: "portal/contact",
    method: "get",
  });
  // Dieren per organisatie → "Pleeggezin actief" vs "Geen dieren op dit moment".
  const animalsQuery = useCustom<PortalAnimals>({ url: "portal/animals", method: "get" });

  if (isLoading) {
    return <LoadingState />;
  }
  if (isError || !data?.data) {
    return <FosterError onRetry={() => void refetch()} onNavigate={onNavigate} />;
  }

  const { organizations } = data.data;
  const orgsMetDieren = new Set(
    (animalsQuery.data?.data?.animals ?? []).map((a) => a.organizationUuid),
  );

  return (
    <div className="foster-contact">
      <header className="fc-header">
        <h1>{t("portal.contact.title")}</h1>
        <p>{t("portal.contact.intro")}</p>
        <p>{t("portal.contact.introSub")}</p>
      </header>

      {organizations.length === 0 ? (
        <EmptyState body={t("portal.contact.empty")} />
      ) : (
        organizations.map((organization) => (
          <OrganizationCard
            key={organization.organizationUuid}
            organization={organization}
            heeftDieren={orgsMetDieren.has(organization.organizationUuid)}
          />
        ))
      )}

      <p className="contact-disclaimer" role="note">
        ⓘ {t("portal.contact.disclaimer")}
      </p>
    </div>
  );
}
